import { activityFetch as fetch } from '../components/feedback/requestActivity'
import { takeSsoHandoff } from '../linkedAppSso';
import React, { useState, createContext, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

export const AuthContext = createContext({
  isLoggedIn: false,
  user: null,
  login: () => {},
  logout: () => {},
  setUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  let navigate = useNavigate();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState('');

  useEffect(() => {
    const clearAuth = () => {
      for (const store of [localStorage, sessionStorage]) {
        store.removeItem('user'); store.removeItem('token');
      }
      setUser(null); setIsLoggedIn(false);
    };
    const init = async () => {
      try {
        const ssoToken = takeSsoHandoff();
        let token = sessionStorage.getItem('token') || localStorage.getItem('token');
        if (ssoToken) {
          // Fresh login selection must never fall back to another saved account.
          clearAuth();
          const response = await fetch('/auth/sso-login', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ sso_token: ssoToken }),
          });
          const data = await response.json();
          if (!response.ok || !data.success || !data.token) {
            setAuthError(data.error || '통합 로그인에 실패했습니다. 앤츠톡에서 다시 열어주세요.');
            return;
          }
          token = data.token;
        }
        if (!token) { clearAuth(); return; }
        const response = await fetch('/auth/me', { headers: { Authorization: 'Bearer ' + token } });
        const data = await response.json();
        if (!response.ok || !data.success || !data.user) { clearAuth(); return; }
        for (const store of [localStorage, sessionStorage]) {
          store.setItem('token', token); store.setItem('user', JSON.stringify(data.user));
        }
        setUser(data.user); setIsLoggedIn(true);
      } catch (_) {
        clearAuth(); setAuthError('통합 로그인 연결에 실패했습니다. 다시 열어주세요.');
      } finally { setLoading(false); }
    };
    init();
  }, []);

  const login = async (userData) => {
    setAuthError('');
    // 두 저장소에 모두 저장
    localStorage.setItem("user", JSON.stringify(userData));
    sessionStorage.setItem("user", JSON.stringify(userData));
    setIsLoggedIn(true);
    setUser(userData);
  };
  
  const logout = async () => {
    // 두 저장소에서 모두 제거 (user + JWT token)
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    setIsLoggedIn(false);
    setUser(null);
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{ isLoggedIn, user, login, logout, setIsLoggedIn, setUser, loading }}
    >
      {authError && <div role="alert" style={{ padding: 12, color: '#b91c1c' }}>{authError}</div>}
      {children}
    </AuthContext.Provider>
  );
};
